import { ExpandLess, ExpandMore } from "@mui/icons-material";
import {
    Collapse,
    List,
    ListItemButton,
    ListItemIcon,
    ListItemText,
    styled,
} from "@mui/material";
import React, { Fragment } from "react";
import { LinkProps, useLocation } from "react-router-dom";
import { useAppSelector } from "../../../redux/hooks";
import {
    SidebarLinkObject,
    SidebarParentObject,
} from "../../../typeDefs/common";
import PrivateSidebarLink from "./PrivateSidebarLink";
import { checkPermission, getSelected, hexToRgb } from "./utils";

const roleClaimType =
    "http://schemas.microsoft.com/ws/2008/06/identity/claims/role";

const StyledListParent = styled(ListItemButton, {
    shouldForwardProp: (p) => !["open", "selected"].includes(p.toString()),
})<{ open: boolean; selected: boolean }>(({ open, selected, theme }) => ({
    paddingTop: "10px",
    paddingLeft: "22px",
    paddingBottom: "12px",
    height: "48px",
    borderRadius: "0px 30px 30px 0px",
    // transition: open ? "0s" : "0.2s",
    // transitionDelay: open ? "0s" : "0.2s",
    color: theme.palette.primary.contrastText,
    overflow: "hidden",
    ".MuiSvgIcon-root": {
        color: theme.palette.primary.contrastText,
    },
    ".MuiListItemText-primary": {
        fontWeight: "bold",
        fontSize: "0.825rem",
    },
    "&:hover": {
        background: `rgba(${hexToRgb("#6F4EE8", "string")}, 0.2)`,
    },
    ...(open && {
        color: "white",
        ".MuiSvgIcon-root": {
            color: "white",
        },
    }),
    marginBottom: 8,
}));

const StyledList = styled(List)(({ theme }) => ({
    paddingTop: "0px",
    marginTop: "0 !important",
    // "a:last-child": {
    //     borderRadius: "0px 0px 24px 24px",
    // },
    ".MuiDivider-root": {
        borderColor: theme.palette.primary.main,
    },
}));

const ExpandableSidebarLink = ({
    links,
    permissions,
    userTypes,
    label,
    icon: Icon,
    hideIfLockedDown,
    displayFlag,
    open = false,
    setOpenMenu = () => console.error("No open function for " + label),
}: SidebarParentObject) => {
    const { [roleClaimType]: userType, permissions: userPermissions } =
        useAppSelector((state) => state.authentication.user);
    const isLockedDown = useAppSelector(
        (state) => state.yardidata.isLockedDown
    );
    const flags = useAppSelector((state) => state.config.flags);

    const location = useLocation();

    if (userTypes && !userTypes.includes(userType)) return null;

    if (permissions && !checkPermission(userPermissions, permissions))
        return null;

    if (hideIfLockedDown && isLockedDown) return null;

    if (displayFlag && displayFlag.length > 0 && flags[displayFlag] === false)
        return null;

    const renderLink = (link: SidebarLinkObject) => (
        <PrivateSidebarLink
            label={link.label}
            route={link.route}
            icon={link.icon}
            hideIfLockedDown={link.hideIfLockedDown}
            displayFlag={link.displayFlag}
            requiredPython={link.requiredPython}
            userTypes={link.userTypes}
            permission={link.permission}
            exact={link.exact}
            nested
        />
    );

    const routes = links.reduce(
        (prev, curr) => prev.concat(curr.route),
        [] as string[]
    );

    const selected = getSelected(routes, false, location.pathname);

    return (
        <Fragment>
            <StyledListParent
                open={open}
                selected={selected}
                onClick={setOpenMenu}
            >
                <ListItemIcon>
                    <Icon color="primary" fontSize="small" selected={open} />
                </ListItemIcon>
                <ListItemText primary={label} />
                {open ? <ExpandLess /> : <ExpandMore />}
            </StyledListParent>
            <Collapse in={open} timeout={200}>
                <StyledList>
                    {links.sort((a, b) => a.order - b.order).map(renderLink)}
                </StyledList>
            </Collapse>
        </Fragment>
    );
};

export default ExpandableSidebarLink;
